import React, {useEffect, useState} from 'react';
import {Close} from '@radix-ui/react-dialog';
import {assignInlineVars} from '@vanilla-extract/dynamic';

import {Button, Link, Modal, Text} from '#component/base';
import {EventType, ModalViewEvent, useAnalytics} from '#component/provider/AnalyticsProvider';
import {Transition} from '#component/util';
import {sprinkles} from '#style/sprinkles';

import {ActionPlanOnboardingProps, Step} from './ActionPlanOnboarding.types';

import * as css from './ActionPlanOnboarding.css';

const steps: Step[] = [
  {
    title: 'Your Action Plan, your way',
    description:
      'Using your unique epigenetic data, we deliver personalized lifestyle recommendations designed to make the biggest impact toward improving your health and longevity.',
    secondaryDescription:
      'Want to adjust your recommendations to better fit your lifestyle or goals? No problem! Your plan is fully customizable.',
    image: '/image/action-plan/onboarding-1.svg',
  },
  {
    title: 'Stay on track',
    description:
      'Check in weekly to track your progress. Re-test your TallyAge™ every three months to measure progress and refine your Action Plan.',
    image: '/image/action-plan/onboarding-2.svg',
  },
];

function ActionPlanOnboarding({
  onClose = () => {},
  onSubmit = () => {},
  saving = false,
  isOpen = false,
}: ActionPlanOnboardingProps) {
  const {track} = useAnalytics();
  const [viewed, setViewed] = useState([false, false]);
  const [index, setIndex] = useState<number>(0);

  useEffect(() => {
    if (isOpen && !viewed[0]) {
      track<ModalViewEvent>({
        evtName: EventType.ModalView,
        modalName: 'Action Plan Onboarding 1',
      });
      setViewed(prev => [true, prev[1]]);
    }
  }, [track, isOpen, viewed]);

  /**
   * Should reset the component to the first step even if mounted
   */
  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (!isOpen) {
      timeout = setTimeout(() => {
        setIndex(0);
      }, 600);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isOpen]);

  const handleClickNext = () => {
    if (isOpen && !viewed[1]) {
      track<ModalViewEvent>({
        evtName: EventType.ModalView,
        modalName: 'Action Plan Onboarding 2',
      });
      setViewed(prev => [prev[0], true]);
    }
    setIndex(index => (index < steps.length ? index + 1 : index));
  };
  const handleClickBack = () => setIndex(index => (index >= 1 ? index - 1 : index));

  if (!steps[index]) {
    return null;
  }
  return (
    <Modal
      layout="fullScreen"
      variant="dark"
      bodyClassName={css.bodyWrapper}
      contentClassName={css.contentWrapper}
      hideCloseButton>
      <div className={css.content}>
        {steps.map((step, i) => (
          <Transition appear className={css.step} key={i} show={i === index}>
            <OnboardingStep
              title={step.title}
              description={step.description}
              secondaryDescription={step.secondaryDescription}
              image={step.image}
            />
          </Transition>
        ))}

        <div className={css.navigationButtons}>
          {index < steps.length - 1 ? (
            <div
              className={sprinkles({
                display: 'flex',
                dir: 'col',
                w: ['full', 'half'],
                content: 'center',
                items: 'center',
              })}>
              <Button
                variant="secondary-dark"
                onClick={handleClickNext}
                color="cotton"
                className={sprinkles({
                  w: 'full',
                })}>
                Next
              </Button>
              <Link
                as={Close}
                onClick={onClose}
                className={sprinkles({
                  mT: 32,
                })}>
                <Text color="cotton" variant="body">
                  Skip to Action Plan
                </Text>
              </Link>
            </div>
          ) : (
            <div
              className={sprinkles({
                display: 'flex',
                dir: ['col', 'row'],
                w: 'full',
                gap: 12,
              })}>
              <Button onClick={handleClickBack} variant="secondary-dark">
                Back
              </Button>
              <Button onClick={onSubmit} loading={saving}>
                Review Action Plan
              </Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function OnboardingStep({image, title, description, secondaryDescription}: Step) {
  return (
    <>
      <div
        aria-hidden
        className={css.stepImage}
        style={assignInlineVars({
          backgroundImage: `url(${image})`,
        })}
      />
      <div className={css.onboardingStep}>
        <Text
          as="h2"
          variant="heading2"
          color="cotton"
          textAlign="center"
          className={sprinkles({
            mB: 32,
            mL: 'auto',
            maxW: 'auto',
          })}>
          {title}
        </Text>
        <Text as="p" color="cotton" textAlign="center">
          {description}
        </Text>
        {secondaryDescription && (
          <Text as="p" color="cotton" textAlign="center" className={sprinkles({pT: 16})}>
            {secondaryDescription}
          </Text>
        )}
      </div>
    </>
  );
}

export {ActionPlanOnboarding};
